import React from 'react'
import { useTranslation } from 'react-i18next'
import { Box } from '@node-space/storybook-components/dist/Box'
import { Button } from '@node-space/ui-components'
import { capitalize } from '@node-space/utils'
import { DisplayErrorMessage } from 'components/Errors/DisplayErrorMessage'
import { RequestError } from 'components/Errors/RequestError'
import { AddCustomerPayload } from 'types/customers'
import { MappedErrorMessageState } from 'types/errors'
import { CustomerDetailsSection } from '../CustomerDetailsSection'

type AddCustomerConfirmProps = {
  onContinue: () => void
  isLoading: boolean
  customer: AddCustomerPayload
  onBack: () => void
  requestError: MappedErrorMessageState
  resetRequestError: () => void
}

export const AddCustomerConfirm = ({
  customer,
  onContinue,
  isLoading,
  onBack,
  requestError,
  resetRequestError,
}: AddCustomerConfirmProps) => {
  const { t } = useTranslation()

  const customerDetails = [
    { label: 'customers.type', value: capitalize(customer?.customerEntityType?.toLowerCase()) },
    { label: 'customers.businessName', value: customer?.businessName },
    { label: 'customers.industry', value: customer?.industryName },
    { label: 'customers.businessDescription', value: customer?.businessDescription },
    { label: 'customers.businessRegistrationNumber', value: customer?.registrationNumber },
    { label: 'customers.businessTaxResidency', value: customer?.businessTaxResidencyName },
    { label: 'customers.businessMonthlyVolume', value: customer?.businessMonthlyVolumeName },
  ]

  const addressDetails = [
    {
      label: 'customers.address.address',
      value: [customer?.addressLine1, customer?.addressLine2, customer?.city, customer?.postalCode]
        ?.filter(Boolean)
        ?.join(', '),
    },
    { label: 'customers.country', value: customer?.countryName },
  ]

  const representativeDetails = [
    { label: 'customers.representative.firstName', value: customer?.firstName },
    { label: 'customers.representative.lastName', value: customer?.lastName },
    { label: 'customers.representative.dateOfBirth', value: customer?.dateOfBirth },
    { label: 'customers.representative.nationality', value: customer?.nationalityName },
  ]

  const handleOnBack = () => {
    resetRequestError()
    onBack()
  }

  return (
    <Box flex direction="col" width={'full'} gapY={20}>
      <Box
        flex
        direction="col"
        padding={24}
        gapY={12}
        background="white"
        border="gray"
        className="h-fit"
        borderRadius={8}
      >
        <CustomerDetailsSection customerDetails={customerDetails} />
        <CustomerDetailsSection
          title={t('customers.registeredAddress')}
          customerDetails={addressDetails}
        />
        <CustomerDetailsSection
          title={t('customers.representative.title')}
          customerDetails={representativeDetails}
        />
        {requestError?.show && (
          <RequestError
            errorCode={requestError?.errorCode}
            sentryTraceId={requestError?.sentryTraceId}
          >
            {requestError?.ErrorMessages && (
              <DisplayErrorMessage
                displaySubTitle
                errorDetails={requestError?.ErrorMessages ?? {}}
              />
            )}
          </RequestError>
        )}
      </Box>
      <Box flex justifyContent="between">
        <Button variant="secondary" onClick={handleOnBack}>
          {t('back')}
        </Button>
        <Button loading={isLoading} onClick={onContinue}>
          {t('confirm')}
        </Button>
      </Box>
    </Box>
  )
}
